import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import ConnectionIcon from '../../components/ConnectionIcon';
import { Clock, CurrencyDollar, ChatCircle, FileText, PlusCircle, User, WhatsappLogo, Play, Thermometer, Megaphone, PhoneCall, WarningCircle, Snowflake, Fire } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.8),
    background: theme.mode === "light" ? '#ffffff' : '#0c0c0e',
    borderRadius: 16,
    boxShadow: theme.mode === "light" ? '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)' : '0 4px 6px -1px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.mode === "light" ? '#f3f4f6' : '#121216'}`,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.mode === "light" ? '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)' : '0 6px 8px -2px rgba(0,0,0,0.4)',
    },
    marginBottom: theme.spacing(1.5),
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(0.5),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
    justifyContent: 'space-between',
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  avatarContainer: {
    position: 'relative',
    marginRight: theme.spacing(1),
  },
  avatar: {
    width: 48,
    height: 48,
  },
  connectionBadge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    backgroundColor: "#25d366",
    color: "white",
    display: "inline",
    padding: "3px",
    borderRadius: "100%",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    "& svg": {
      fill: "#ffffff",
      color: "#ffffff"
    }
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.mode === "light" ? "#0B141A" : "#ffffff",
    lineHeight: '1.2',
  },
  ticketNumber: {
    fontSize: '11px',
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    marginTop: '2px',
  },
  rightHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  placeholderBadge: {
    fontSize: '0.65rem',
    backgroundColor: theme.mode === 'light' ? '#f3f4f6' : '#374151',
    color: theme.mode === 'light' ? '#4b5563' : '#d1d5db',
    padding: '2px 8px',
    borderRadius: '6px',
    fontWeight: 600,
    textTransform: 'uppercase',
    border: `1px solid ${theme.mode === 'light' ? '#e5e7eb' : '#4b5563'}`,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  scoreBadgeVeryHot: { backgroundColor: '#7f1d1d', color: '#ffffff', borderColor: '#450a0a' },
  scoreBadgeHot: { backgroundColor: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' },
  scoreBadgeWarm: { backgroundColor: '#fef3c7', color: '#d97706', borderColor: '#fde68a' },
  scoreBadgeCold: { backgroundColor: '#e0f2fe', color: '#2563eb', borderColor: '#bae6fd' },
  scoreBadgeFrozen: { backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#d1d5db' },
  pulseAnimation: {
    animation: "$pulse 1.5s infinite",
  },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(220, 38, 38, 0.7)" },
    "70%": { transform: "scale(1.05)", boxShadow: "0 0 0 10px rgba(220, 38, 38, 0)" },
    "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(220, 38, 38, 0)" },
  },
  adsBadge: {
    backgroundColor: '#f3e8ff',
    color: '#4f46e5',
    borderColor: '#e9d5ff',
  },
  lastMessageTime: {
    fontSize: '0.8rem',
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
  },
  lastMessageTimeUnread: {
    fontSize: '0.8rem',
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  metricsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  waitTime: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: theme.mode === 'light' ? '#ea580c' : '#bcbcbc',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  valueBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: theme.mode === 'light' ? '#02b881' : '#02b881',
    backgroundColor: theme.mode === 'light' ? '#02b88124' : '#00ff7b17',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 600,
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  divider: {
    height: '0.5px',
    backgroundColor: theme.mode === 'light' ? '#e5e7eb' : '#4b5563',
    border: 'none',
  },
  messageBox: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.mode === 'light' ? '#f8fafc' : 'rgba(0, 0, 0, 0.05)',
    border: `1px solid ${theme.mode === 'light' ? '#f1f5f9' : '#121216'}`,
    padding: '10px',
    borderRadius: '12px',
  },
  messageBoxFromMe: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.mode === 'light' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
    border: `1px solid ${theme.mode === 'light' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)'}`,
    padding: '10px',
    borderRadius: '12px',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.mode === 'light' ? '#6b7280' : '#9ca3af',
    textTransform: 'uppercase',
  },
  messageHeaderFromMe: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.mode === 'light' ? '#15803d' : '#4ade80',
    textTransform: 'uppercase',
    flexDirection: 'row-reverse',
  },
  messageContentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  messageContentRowFromMe: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    flexDirection: 'row-reverse',
  },
  messageText: {
    fontSize: '0.85rem',
    color: theme.mode === 'light' ? '#2563eb' : '#60a5fa',
    opacity: 0.9,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  messageTextFromMe: {
    fontSize: '0.85rem',
    color: theme.mode === 'light' ? '#15803d' : '#4ade80',
    opacity: 0.9,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    textAlign: 'right',
  },
  messageTime: {
    fontSize: '0.7rem',
    color: theme.mode === 'light' ? '#9ca3af' : '#6b7280',
    flexShrink: 0,
  },
  noteBox: {
    marginTop: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.mode === 'light' ? '#fff7ed' : '#211c140d',
    border: `1px solid ${theme.mode === 'light' ? '#ffedd5' : '#7f5327'}`,
    padding: '10px',
    borderRadius: '12px',
  },
  noteHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.mode === 'light' ? '#ea580c' : '#9ca3af',
    textTransform: 'uppercase',
  },
  noteText: {
    fontSize: '0.8rem',
    color: theme.mode === 'light' ? '#374151' : '#d1d5db',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.3',
  },
  noteFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  notedBy: {
    fontSize: '0.65rem',
    color: theme.mode === 'light' ? '#6b7280' : '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  notedByHighlight: {
    fontWeight: 600,
    color: theme.mode === 'light' ? '#374151' : '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    background: theme.mode === 'light' ? '#fff' : '#374151',
    padding: '2px 4px',
    borderRadius: '4px',
    border: `1px solid ${theme.mode === 'light' ? '#e5e7eb' : '#4b5563'}`,
  },
  addNoteButton: {
    fontSize: '0.65rem',
    color: theme.mode === 'light' ? '#ea580c' : '#9ca3af',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    }
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
    marginTop: "8px",
  },
  btnPrimary: {
    display: "flex",
    alignItems: 'center',
    justifyContent: "center",
    gap: "6px",
    backgroundColor: "#16a34a", // bg-green-600
    color: "white",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "#15803d", // hover:bg-green-700
    }
  },
  btnSecondary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    backgroundColor: "transparent",
    color: theme.mode === "light" ? "#374151" : "#e5e7eb",
    border: `1px solid ${theme.mode === "light" ? "#e5e7eb" : "#4b5563"}`,
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    '&:hover': {
      backgroundColor: theme.mode === "light" ? "#f9fafb" : "#374151",
    },
  },
  btnDashed: {
    gridColumn: "span 2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    backgroundColor: theme.mode === "light" ? "#f9fafb" : "#0c0c0e", // Mesmo fundo da Última Mensagem
    color: theme.mode === "light" ? "#4b5563" : "#9ca3af",
    border: `2px dashed ${theme.mode === "light" ? "#9ca3af" : "#4b5563"}`,
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.15)",
    }
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  removeValueButton: {
    padding: 0,
    marginLeft: theme.spacing(1),
    color: theme.palette.error.main,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  dialogPaper: {
    borderRadius: '10px',
  },
  dialogButton: {
    borderRadius: '10px',
  },
}));

// Micro-componente isolado para evitar re-renderização de todo o Card
const WaitTimer = ({ updatedAt }) => {
  const [waitTime, setWaitTime] = useState('00:00:00');

  useEffect(() => {
    const updateWaitTime = () => {
      if (!updatedAt) return;
      
      const now = new Date();
      const updated = parseISO(updatedAt);
      const diffMs = Math.max(0, now - updated); // Evita valores negativos
      const totalMinutes = Math.floor(diffMs / 60000);
      
      const days = Math.floor(totalMinutes / (24 * 60));
      const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
      const minutes = totalMinutes % 60;
      
      setWaitTime(`${String(days).padStart(2, '0')}:${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
    };

    updateWaitTime();
    const interval = setInterval(updateWaitTime, 60000); // Atualiza apenas esse trecho de texto

    return () => clearInterval(interval);
  }, [updatedAt]);

  return <>{waitTime}</>;
};

const KanbanCard = ({ ticket, index, updateTicket }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState('');
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [newNote, setNewNote] = useState('');

  // Busca a lista de fluxos criados quando o modal é aberto
  useEffect(() => {
    if (flowModalOpen) {
      const fetchFlows = async () => {
        try {
          const { data } = await api.get('/flowbuilder'); // Rota de busca dos fluxos
          // Trata o retorno caso a API empacote os dados em data.flowbuilders ou array direto
          const flowsData = data.flowbuilder || data.flows || (Array.isArray(data) ? data : []);
          setFlows(flowsData);
        } catch (err) {
          console.error('Erro ao buscar fluxos:', err);
          toast.error('Erro ao carregar a lista de fluxos.');
        }
      };
      fetchFlows();
    }
  }, [flowModalOpen]);

  // Função responsável por disparar o fluxo
  const handleSendFlow = async () => {
    if (!selectedFlow) {
      toast.warning('Por favor, selecione um fluxo!');
      return;
    }
    try {
      await api.post(`/tickets/${ticket.id}/flow`, {
        flowId: selectedFlow,
      });
      toast.success('Fluxo disparado com sucesso!');
      setFlowModalOpen(false);
      setSelectedFlow('');
    } catch (err) {
      console.error('Erro ao disparar fluxo:', err);
      toast.error('Erro ao disparar o fluxo.');
    }
  };

  // Funções para adicionar anotação
  const handleOpenNoteModal = () => {
    setNewNote(''); // Limpa ou carrega a nota existente se desejar
    setNoteModalOpen(true);
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) {
      toast.error('A anotação não pode estar vazia.');
      return;
    }

    try {
      // 1. Envia a anotação como mensagem interna no chat do ticket
      const messagePayload = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `*Anotação (Kanban):*\n${newNote}`,
        isPrivate: "true", // Define como mensagem interna
      };
      await api.post(`/messages/${ticket.id}`, messagePayload);

      // 2. Salva a anotação nos campos adicionais do contato para persistir no Kanban (F5)
      const updatedExtraInfo = [...customFields];
      const existingNoteIndex = updatedExtraInfo.findIndex(field => field.name === 'anotacao_kanban');
      
      if (existingNoteIndex !== -1) {
        updatedExtraInfo[existingNoteIndex] = { ...updatedExtraInfo[existingNoteIndex], value: newNote };
      } else {
        updatedExtraInfo.push({ name: 'anotacao_kanban', value: newNote });
      }
      await api.put(`/contacts/${ticket.contact.id}`, { extraInfo: updatedExtraInfo });

      // 3. Atualiza a interface local para feedback imediato
      ticket.contact.extraInfo = updatedExtraInfo;
      ticket.lastNote = newNote;
      ticket.notedBy = user?.name || "Você";
      updateTicket({ ...ticket });

      toast.success('Anotação adicionada e salva no chat!');
      setNoteModalOpen(false);
      setNewNote(''); // Reseta o campo
    } catch (err) {
      console.error(err);
      toast.error('Erro ao adicionar anotação.');
    }
  };

  const handleCardClick = () => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  const timeString = isSameDay(parseISO(ticket.updatedAt), new Date())
    ? format(parseISO(ticket.updatedAt), 'HH:mm')
    : format(parseISO(ticket.updatedAt), 'dd/MM/yyyy');

  const customFields = ticket.contact.extraInfo || [];
  const opportunityValue = ticket.valorVenda !== null && ticket.valorVenda !== undefined ? parseFloat(ticket.valorVenda) : null;
  const hasOpportunityValue = opportunityValue !== null && !isNaN(opportunityValue);

  // Busca a anotação salva nos campos extras do contato
  const noteField = customFields.find(field => field.name === 'anotacao_kanban');

  // Define a anotação persistida ou cai para o ultimo log do ticket
  const lastNoteText = ticket.lastNote || (noteField ? noteField.value : (ticket.logs && ticket.logs.length > 0 ? ticket.logs[ticket.logs.length - 1].log : 'Nenhuma anotação ou log'));
  const notedByUser = ticket.notedBy || ticket.user?.name || "Aguardando";

  const handleOpenModal = () => {
    setNewValue(hasOpportunityValue ? opportunityValue.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const removeTicketValue = async () => {
    try {
      await api.put(`/tickets/${ticket.id}`, { valorVenda: null });
      ticket.valorVenda = null;
      updateTicket({ ...ticket });
      toast.success('Valor removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover o valor do ticket:', error);
      toast.error('Erro ao remover o valor do ticket.');
    }
  };

  const handleSaveValue = async () => {
    try {
      const numericValue = parseFloat(newValue);
      const valueToSave = isNaN(numericValue) ? null : numericValue;
      await api.put(`/tickets/${ticket.id}`, { valorVenda: valueToSave });
      ticket.valorVenda = valueToSave;
      updateTicket({ ...ticket });
      toast.success('Valor atualizado com sucesso!');
      setOpen(false);
    } catch (error) {
      console.error('Erro ao atualizar o valor do ticket:', error);
      toast.error('Erro ao salvar o valor do ticket.');
    }
  };

  // Determina quem enviou a última mensagem para a interface visual
  let isFromMe = false;
  if (ticket.lastMessageFromMe !== undefined) {
    isFromMe = ticket.lastMessageFromMe;
  } else if (ticket.messages && ticket.messages.length > 0) {
    isFromMe = ticket.messages[ticket.messages.length - 1].fromMe;
  } else if (ticket.lastMessage && ticket.user?.name && ticket.lastMessage.startsWith(`*${ticket.user.name}`)) {
    isFromMe = true;
  }
  const messageSenderName = isFromMe ? (ticket.user?.name ? ticket.user.name.split(' ')[0] : "Atendente") : (ticket.contact?.name ? ticket.contact.name.split(' ')[0] : "Cliente");

  // Extraindo os dados do Lead Scoring enviados pelo backend
  const scoringInfo = ticket.scoringInfo || { leadScore: 0, temperatureStatus: 'cold', isFromAds: false, actionAlerts: {} };

  return (
    <Draggable draggableId={`ticket-${ticket.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.card}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <div className={classes.header}>
            <div className={classes.leftHeader}>
              <div className={classes.avatarContainer}>
                <Avatar src={ticket.contact.urlPicture} className={classes.avatar} />
                {ticket.channel && (
                  <div className={classes.connectionBadge}>
                    <ConnectionIcon width="12" height="12" connectionType={ticket.channel} />
                  </div>
                )}
              </div>
              <div className={classes.contactInfo}>
                <Tooltip title={ticket.contact.name}>
                  <Typography className={classes.cardTitle}>
                    {ticket.contact.name?.substring(0, 15) || ' '}
                  </Typography>
                </Tooltip>
                <Typography className={classes.ticketNumber}>
                  Ticket nº {ticket.id}
                </Typography>
              </div>
            </div>
            <div className={classes.rightHeader} style={{ display: 'flex', flexDirection: 'row', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
               {/* LEAD SCORING: Badge de Origem (Tráfego Pago) - {scoringInfo.isFromAds && (
                <Tooltip title="Origem: Tráfego Pago (Ads)">
                  <span className={`${classes.placeholderBadge} ${classes.adsBadge}`}>
                    <Megaphone size={12} weight="bold" /> Ads
                  </span>
                </Tooltip>
               )} */}
               
               {/* LEAD SCORING: Badge de Temperatura - {scoringInfo.actionAlerts?.criticalCooldown ? (
                  <Tooltip title="Esfriamento Crítico atingido (Lead Congelado)">
                    <span className={`${classes.placeholderBadge} ${classes.scoreBadgeFrozen}`}>
                      <Snowflake size={12} weight="bold" /> Base Fria
                    </span>
                  </Tooltip>
               ) : (
                 <Tooltip title={`Score: ${scoringInfo.leadScore}/100`}>
                     <span className={`${classes.placeholderBadge} ${
                      scoringInfo.temperatureStatus === 'very_hot' ? classes.scoreBadgeVeryHot :
                      scoringInfo.temperatureStatus === 'hot' ? classes.scoreBadgeHot : 
                      scoringInfo.temperatureStatus === 'warm' ? classes.scoreBadgeWarm : 
                      classes.scoreBadgeCold
                    }`}>
                       {scoringInfo.temperatureStatus === 'very_hot' ? <Fire size={12} weight="bold" /> : <Thermometer size={12} weight="bold" />} {scoringInfo.leadScore}
                     </span>
                 </Tooltip>
               )} */}

            </div>
          </div>
          <div className={classes.metricsRow}>
            {/* LEAD SCORING: Alerta de tempo de espera (SLA) */}
            <div className={classes.waitTime} /* style={scoringInfo.actionAlerts?.followUpWarning ? { color: '#dc2626', fontWeight: 'bold' } : {}} */>
              {/* {(scoringInfo.actionAlerts?.slaFollowUpBroken || scoringInfo.actionAlerts?.followUpWarning) ? <WarningCircle size={14} weight="bold" color="#dc2626" /> : <Clock size={14} weight="bold" />} */}
              <Clock size={14} weight="bold" />
              <span>Espera: <WaitTimer updatedAt={ticket.updatedAt} /></span>
            </div>
            <div className={classes.valueContainer}>
              <div className={classes.valueBadge} onClick={handleOpenModal}>
                <CurrencyDollar size={14} weight="bold" />
                <span>
                  {hasOpportunityValue
                    ? `R$ ${opportunityValue.toFixed(2)}`
                    : 'R$ 0,00'}
                </span>
              </div>
              {hasOpportunityValue && (
                <Tooltip title={"Remover"}>
                  <IconButton
                    className={classes.removeValueButton}
                    onClick={removeTicketValue}
                    size="small"
                    style={{ padding: 2, marginLeft: 4 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
          <Divider className={classes.divider} />
          <div className={isFromMe ? classes.messageBoxFromMe : classes.messageBox}>
            <div className={isFromMe ? classes.messageHeaderFromMe : classes.messageHeader}>
              <ChatCircle size={18} color={isFromMe ? "#22c55e" : "#3b82f6"} />
              Últ. msg: {messageSenderName}
            </div>
            <div className={isFromMe ? classes.messageContentRowFromMe : classes.messageContentRow}>
              <Tooltip title={ticket.lastMessage || ' '}>
                <Typography className={isFromMe ? classes.messageTextFromMe : classes.messageText}>
                  {ticket.lastMessage?.substring(0, 30) || ' '}
                </Typography>
              </Tooltip>
              <span className={classes.messageTime}>{timeString}</span>
            </div>
          </div>
          <div className={classes.noteBox}>
            <div className={classes.noteHeader}>
              <FileText size={14} weight="fill" />
              Última anotação
            </div>
            <Typography className={classes.noteText}>
              {lastNoteText}
            </Typography>
            <div className={classes.noteFooter}>
              <div className={classes.notedBy}>
                Anotado por: 
                <span className={classes.notedByHighlight}>
                  <User size={10} weight="bold" /> {notedByUser}
                </span>
              </div>
              <button className={classes.addNoteButton} onClick={(e) => { e.stopPropagation(); handleOpenNoteModal(); }}>
                <PlusCircle size={14} weight="bold" /> Add
              </button>
            </div>
          </div>
          <div className={classes.actionsGrid}>
            <button className={classes.btnPrimary} onClick={handleCardClick}>
              <WhatsappLogo size={16} weight="bold" />
              Ir para Ticket
            </button>
            {/* LEAD SCORING: Alerta de Ação (Ligar Urgente) - {scoringInfo.actionAlerts?.needToCall ? (
              <button className={`${classes.btnSecondary} ${classes.pulseAnimation}`} style={{ color: '#dc2626', borderColor: '#fca5a5', backgroundColor: '#fef2f2' }} onClick={(e) => { e.stopPropagation(); }}>
                <PhoneCall size={16} weight="bold" />
                Ligar Urgente
              </button>
            ) : ( */}
              <button className={classes.btnSecondary} onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); setFlowModalOpen(true); }}>
                <Play size={16} weight="fill" color="#22c55e" />
                Disparar Fluxo
              </button>
            {/* )} */}
            <button className={classes.btnDashed} onClick={(e) => { e.stopPropagation(); e.currentTarget.blur(); handleOpenModal(); }}>
              <PlusCircle size={16} weight="bold" />
              Adicionar valor
            </button>
          </div>
          <Dialog
            open={open}
            onClose={handleCloseModal}
            classes={{ paper: classes.dialogPaper }}
          >
            <DialogTitle>{hasOpportunityValue ? 'Editar' : 'Atribuir'} Valor da Oportunidade</DialogTitle>
            <DialogContent>
              <TextField
                label="Valor"
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseModal}
                color="secondary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveValue}
                color="primary"
                variant="outlined"
                className={classes.dialogButton}
              >
                Salvar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Pop-up do FlowBuilder */}
          <Dialog
            open={flowModalOpen}
            onClose={() => setFlowModalOpen(false)}
            classes={{ paper: classes.dialogPaper }}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Disparar Fluxo</DialogTitle>
            <DialogContent>
              <Typography variant="body2" gutterBottom>
                Selecione o fluxo do FlowBuilder que deseja disparar para este contato.
              </Typography>
              
              <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: '16px' }}>
                <InputLabel>Selecione um Fluxo</InputLabel>
                <Select
                  value={selectedFlow}
                  onChange={(e) => setSelectedFlow(e.target.value)}
                  label="Selecione um Fluxo"
                >
                  {flows.map((flow) => (
                    <MenuItem key={flow.id} value={flow.id}>
                      {flow.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setFlowModalOpen(false)}
                className={classes.dialogButton}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendFlow}
                color="primary"
                variant="contained"
                className={classes.dialogButton}
              >
                Disparar
              </Button>
            </DialogActions>
          </Dialog>

          {/* Pop-up de Adicionar Anotação */}
          <Dialog
            open={noteModalOpen}
            onClose={() => setNoteModalOpen(false)}
            classes={{ paper: classes.dialogPaper }}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Adicionar Anotação</DialogTitle>
            <DialogContent>
              <TextField
                label="Escreva sua anotação"
                multiline
                rows={4}
                fullWidth
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNoteModalOpen(false)} className={classes.dialogButton}>
                Cancelar
              </Button>
              <Button onClick={handleSaveNote} color="primary" variant="contained" className={classes.dialogButton}>
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
